import { useEffect, useMemo, useState } from 'react';
import { openBridgeOptions } from './api/extensionClient';
import { AlertCenter } from './components/AlertCenter';
import { ExtensionSetup } from './components/ExtensionSetup';
import { Header, type AppView } from './components/Header';
import { IntegrationBar } from './components/IntegrationBar';
import { MarketPulse } from './components/MarketPulse';
import { MarketScanner } from './components/MarketScanner';
import { StrategyPanel } from './components/StrategyPanel';
import { TradingChart } from './components/TradingChart';
import { useAlerts } from './hooks/useAlerts';
import { useApiBridge } from './hooks/useApiBridge';
import { useMarketDashboard } from './hooks/useMarketDashboard';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import { usePwaInstall } from './hooks/usePwaInstall';
import { useWatchlist } from './hooks/useWatchlist';
import { INTERVAL_OPTIONS } from './lib/intervals';
import type { MarketInterval } from './types/market';

export default function App() {
  const { symbols, addSymbol, removeSymbol, maxSymbols } = useWatchlist();
  const { status: bridgeStatus, version: bridgeVersion, check: checkBridge } = useApiBridge();
  const { canInstall, installed, install } = usePwaInstall();
  const online = useOnlineStatus();
  const [selectedSymbol, setSelectedSymbol] = useState(symbols[0] ?? 'AAPL');
  const [interval, setInterval] = useState<MarketInterval>(() => {
    return (localStorage.getItem('gmv-chart-interval') as MarketInterval) || '1D';
  });
  const [extendedHours, setExtendedHours] = useState(() => localStorage.getItem('gmv-chart-ext') === 'true');
  const [view, setView] = useState<AppView>('dashboard');
  const [pollMs, setPollMs] = useState(() => Number(localStorage.getItem('goldmine-vision-poll-ms')) || 15_000);
  const [autoRefresh, setAutoRefresh] = useState(() => localStorage.getItem('goldmine-vision-auto-refresh') !== 'false');

  const intervalConfig = useMemo(
    () => INTERVAL_OPTIONS.find((item) => item.interval === interval) ?? INTERVAL_OPTIONS[0],
    [interval]
  );

  const dataEnabled = bridgeStatus === 'connected' && online;
  const { snapshots, refreshAll } = useMarketDashboard(
    symbols,
    intervalConfig.interval,
    intervalConfig.range,
    extendedHours,
    dataEnabled,
    autoRefresh ? pollMs : 24 * 60 * 60 * 1000
  );
  const alerts = useAlerts(snapshots);

  useEffect(() => {
    if (!symbols.includes(selectedSymbol)) setSelectedSymbol(symbols[0] ?? 'AAPL');
  }, [selectedSymbol, symbols]);

  useEffect(() => localStorage.setItem('goldmine-vision-poll-ms', String(pollMs)), [pollMs]);
  useEffect(() => localStorage.setItem('goldmine-vision-auto-refresh', String(autoRefresh)), [autoRefresh]);
  useEffect(() => localStorage.setItem('gmv-chart-interval', interval), [interval]);
  useEffect(() => localStorage.setItem('gmv-chart-ext', String(extendedHours)), [extendedHours]);

  const selectedSnapshot = snapshots[selectedSymbol];
  const candles = selectedSnapshot?.data?.candles ?? [];

  return (
    <div className="app-shell">
      <Header
        interval={interval}
        onIntervalChange={setInterval}
        onAddSymbol={addSymbol}
        onRefresh={() => void refreshAll(true)}
        symbolCount={symbols.length}
        maxSymbols={maxSymbols}
        view={view}
        onViewChange={setView}
        alertCount={alerts.activeCount}
        bridgeStatus={bridgeStatus}
        bridgeVersion={bridgeVersion}
        onCheckBridge={() => void checkBridge()}
        onOpenPermissions={() => void openBridgeOptions()}
        canInstallPwa={canInstall}
        pwaInstalled={installed}
        onInstallPwa={() => void install()}
        online={online}
        pollMs={pollMs}
        onPollMsChange={setPollMs}
        autoRefresh={autoRefresh}
        onAutoRefreshChange={setAutoRefresh}
      />

      <main>


        {bridgeStatus === 'missing' ? <ExtensionSetup /> : null}
        {!online ? <div className="offline-banner">You are offline. GoldMine Vision can open from its PWA cache, but live market data needs an internet connection.</div> : null}

        <MarketPulse snapshots={snapshots} />

        {view === 'dashboard' ? (
          <>
            <section className="workspace-grid" style={{ display: 'flex', flexDirection: 'column', minHeight: '800px', flex: 1, gap: '16px' }}>
              <div className="chart-panel" style={{ flex: 1, display: 'flex', minHeight: '0', padding: 0 }}>
                {candles.length ? (
                  <TradingChart 
                    symbol={selectedSymbol} 
                    candles={candles} 
                    analysis={selectedSnapshot?.analysis}
                    watchlistSymbols={symbols}
                    onAddSymbol={addSymbol}
                    onRemoveSymbol={removeSymbol}
                    onSelectSymbol={setSelectedSymbol}
                    snapshots={snapshots}
                    interval={interval}
                    onIntervalChange={setInterval}
                    extendedHours={extendedHours}
                    onExtendedHoursChange={setExtendedHours}
                  />
                ) : (
                  <div className="chart-empty">
                    {selectedSnapshot?.error
                      ? selectedSnapshot.error
                      : bridgeStatus === 'missing'
                        ? 'Install GMV Bridge to load Yahoo market data.'
                        : !online
                          ? 'Live market data is unavailable while offline.'
                          : 'Waiting for Yahoo market data…'}
                  </div>
                )}
              </div>
            </section>
          </>
        ) : null}

        {view === 'scanner' ? (
          <MarketScanner snapshots={snapshots} onSelect={(symbol) => { setSelectedSymbol(symbol); setView('dashboard'); }} />
        ) : null}

        {view === 'alerts' ? (
          <AlertCenter
            symbols={symbols}
            rules={alerts.rules}
            history={alerts.history}
            onAdd={alerts.addRule}
            onRemove={alerts.removeRule}
            onToggle={alerts.toggleRule}
            onClearHistory={alerts.clearHistory}
            onEnableNotifications={alerts.requestNotifications}
          />
        ) : null}

        <footer className="app-footer">
          <strong>GoldMine Vision</strong> uses Yahoo Finance as its first MarketData provider through GMV Bridge. Additional providers can reuse the same normalized candle contract. Signals and analytics are experimental research tools, not investment advice or trade-execution instructions.
        </footer>
      </main>
    </div>
  );
}
